// src/AppRouter.tsx - FINAL REVIEWED VERSION
import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from './routes';
import ProtectedRoute from './ProtectedRoute';
import MainLayout from '@/layouts/MainLayout';
import { Suspense, lazy } from 'react';
import Preloader from '@/components/Preloader';
import { useAuth } from '@/context/AuthContext';
import ScrollToTop from '@/components/ScrollToTop';
import OtherLayout from '@/layouts/OtherLayout';
import AuthLayout from '@/layouts/AuthLayout';

const HomeRedirect = lazy(() => import('@/components/HomeRedirect'));

// Map từ role sang nhóm quyền (groups)
const ROLE_TO_GROUP_MAP: Record<string, string[]> = {
    'right_owner': ['PRIMARY_USERS'],
    'licensee': ['PRIMARY_USERS'], 
    'admin': ['ADMIN_USERS'],
    'monitor': ['MONITORING_TEAM'],
    'legal': ['LEGAL_TEAM', 'SUPPORT_MONITORING'],
    'staff': ['ADMIN_USERS'],
    'teacher': ['EDUCATION_USERS', 'TEACHERS'],
    'student': ['EDUCATION_USERS', 'STUDENTS'],
    'education_admin': ['EDUCATION_USERS', 'EDUCATION_ADMIN'],
    'business_developer': ['BUSINESS_DEVELOPMENT'],
    'security_analyst': ['RISK_SECURITY'],
    'support_specialist': ['SUPPORT_MONITORING'],
};

const AppRouter = () => {
    const { isAuthenticated, isLoading } = useAuth();

    const isAuthRoute = (path: string): boolean => {
        const authPatterns = [
            '/login',
            '/register',
            '/register-admin', 
            '/forgot-password',
            '/register-success',
            '/unauthorized',
        ];
        return authPatterns.includes(path);
    };

    // ✅ FIX: Cải tiến cách define public routes với pattern matching
    const isPublicRoute = (path: string): boolean => {
        const publicPatterns = [
            '/home',
            '/vietrro',
            '/works',
            '/users', 
            '/violations',
            '/identity-protection',
            
            // Public marketplace routes
            '/marketplace',
            '/marketplace/browse',
            '/marketplace/unclaimed',
            '/marketplace/verify',
            
            // Public certificate verification only
            //'/certificates/verification',
            
            // Dynamic public routes
            /^\/works\/[^\/]+$/,        // /works/:id
            /^\/users\/[^\/]+$/,        // /users/:id
            /^\/violations\/[^\/]+$/,   // /violations/:id
            /^\/marketplace\/content\/[^\/]+$/, // /marketplace/content/:id
        ];

        return publicPatterns.some(pattern => {
            if (typeof pattern === 'string') {
                return pattern === path;
            } else {
                return pattern.test(path);
            }
        });
    };

    // Chuyển đổi từ roles sang groups
    const getGroupsFromRoles = (roles?: string[]): string[] => {
        if (!roles || roles.length === 0) return [];

        const groups = new Set<string>();
        roles.forEach(role => {
            const mappedGroups = ROLE_TO_GROUP_MAP[role] || [];
            mappedGroups.forEach(group => groups.add(group));
        });

        return Array.from(groups);
    };

    // Kiểm tra route có cần special layout không
    const needsSpecialLayout = (path: string): boolean => {
        // UserWall routes cần UserWallLayout
        if (path.includes('/user/') && (path.includes('/wall') || path.includes('/works') || 
            path.includes('/licenses') || path.includes('/collections') || path.includes('/about'))) {
            return true;
        }
        return false;
    };

    if (isLoading) {
        return <Preloader />;
    }

    // Phân loại routes
    const publicRoutes = ROUTES.filter(route => isPublicRoute(route.path as string));
    const protectedRoutes = ROUTES.filter(route => 
        !isPublicRoute(route.path as string) && route.path !== '*'
    );
    const fallbackRoute = ROUTES.find(route => route.path === '*');

    return (
        <Suspense fallback={<Preloader />}>
            <ScrollToTop />
            
            <Routes>
                {/* Home redirect route */}
                <Route path="/" element={<HomeRedirect />} />

                {ROUTES.filter(route => isAuthRoute(route.path as string)).map((route) => {
                    return (
                        <Route
                            key={route.path as string}
                            path={route.path as string}
                            element={
                                <AuthLayout>
                                    {route.element}
                                </AuthLayout>

                            }
                        />
                    )
                })};

                {/* ✅ FIX: Improved public routes handling */}
                {ROUTES.filter(route => isPublicRoute(route.path as string)).map((route) => {
                    return (
                        <Route
                            key={route.path as string}
                            path={route.path as string}
                            element={
                                <OtherLayout>
                                    {route.element}
                                </OtherLayout>

                            }
                        />
                    )
                })}

                {/* Protected routes - cần authentication và authorization */}
                {protectedRoutes.map((route) => {
                    const allowedGroups = getGroupsFromRoles(route.roles);
                    const routePath = route.path as string;
                    
                    return (
                        <Route
                            key={routePath}
                            path={routePath}
                            element={
                                <ProtectedRoute allowedGroups={allowedGroups}>
                                    {needsSpecialLayout(routePath) ? (
                                        // UserWall và similar routes dùng special layout
                                        route.element
                                    ) : (
                                        // Standard protected routes dùng MainLayout
                                        <MainLayout>
                                            {route.element}
                                        </MainLayout>
                                    )}
                                </ProtectedRoute>
                            }
                        />
                    );
                })}

                {/* 404 fallback route */}
                {fallbackRoute && (
                    <Route
                        path="*"
                        element={fallbackRoute.element}
                    />
                )}
            </Routes>
        </Suspense>
    );
};

export default AppRouter;
